/**
 * TransformPanel — transform controls, presets, display settings.
 * Replaces window.TransformPanel.
 */
import { useState, type FC } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './Icon';
import type { Transform } from '../stores/types';

interface Props {
  transform: Transform;
  onUpdateTransform: (t: Transform) => void;
  dpi: number;
  onDPIChange?: (dpi: number) => void;
  activeProductId: string | null;
  isActive: boolean;
  mockupsPerRow: number;
  setMockupsPerRow?: (n: number) => void;
  presets: Record<string, unknown>;
  onSavePreset?: (name: string, scope: string) => void;
  onDeletePreset?: (name: string) => void;
  onApplyPreset?: (name: string, applyToAll?: boolean) => void;
  saveStatus?: 'saved' | 'saving' | 'error';
  maskColor: string | null;
  setMaskColor?: (c: string | null) => void;
  onCopyTransform?: () => void;
  onPasteTransform?: () => void;
}

export const TransformPanel: FC<Props> = ({
  transform,
  onUpdateTransform,
  dpi,
  onDPIChange,
  isActive,
  mockupsPerRow,
  setMockupsPerRow,
  presets,
  onSavePreset,
  onDeletePreset,
  onApplyPreset,
  saveStatus = 'saved',
  maskColor,
  setMaskColor,
  onCopyTransform,
  onPasteTransform,
}) => {
  const [presetName, setPresetName] = useState('');
  const [copyStatus, setCopyStatus] = useState('idle');
  const [pasteStatus, setPasteStatus] = useState('idle');
  const [sections, setSections] = useState({ display: true, transform: true, presets: true });
  const toggleSection = (key: string) =>
    setSections((prev) => ({ ...prev, [key]: !prev[key as keyof typeof prev] }));

  const t: Transform = {
    x: Number.isFinite(transform?.x) ? transform.x : 0,
    y: Number.isFinite(transform?.y) ? transform.y : 0,
    scale: Math.min(10, Math.max(0.05, Number.isFinite(transform?.scale) ? transform.scale : 0.5)),
    rotation: Number.isFinite(transform?.rotation) ? transform.rotation : 0,
  };

  const rotateBy = (deg: number) => onUpdateTransform({ ...t, rotation: (t.rotation || 0) + deg });

  const handleSaveClick = (scope = 'single') => {
    if (!presetName.trim()) return alert('Введите имя конфига');
    onSavePreset?.(presetName.trim(), scope);
    setPresetName('');
  };

  const copyTransform = () => {
    if (onCopyTransform) {
      onCopyTransform();
      setCopyStatus('success');
      setTimeout(() => setCopyStatus('idle'), 1000);
    }
  };

  const pasteTransform = () => {
    if (onPasteTransform) {
      onPasteTransform();
      setPasteStatus('success');
      setTimeout(() => setPasteStatus('idle'), 1000);
    }
  };

  return (
    <div className="tp-root">
      {/* Save Status Portal */}
      {(saveStatus === 'saving' || saveStatus === 'error') &&
        createPortal(
          <div
            className={`fixed bottom-20 left-4 z-[9999] flex items-center gap-2 px-4 py-2 text-sm rounded-lg shadow-xl border transition-all duration-300 ${
              saveStatus === 'saving'
                ? 'bg-slate-900/90 text-blue-400 border-blue-500/30'
                : 'bg-slate-900/90 text-red-400 border-red-500/30'
            }`}
          >
            <Icon
              name={saveStatus === 'saving' ? 'loader-2' : 'alert-circle'}
              className={`w-4 h-4 ${saveStatus === 'saving' ? 'animate-spin' : ''}`}
            />
            <span>{saveStatus === 'saving' ? 'Сохранение...' : 'Ошибка сохранения'}</span>
          </div>,
          document.body,
        )}

      {/* Display Section */}
      <div className="tp-section">
        <div onClick={() => toggleSection('display')} className="tp-section-header" role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && toggleSection('display')}>
          <div className="flex items-center gap-2">
            <Icon name="settings-2" className="w-3.5 h-3.5 text-indigo-400" />
            <span>Отображение</span>
          </div>
          <Icon
            name="chevron-down"
            className={`w-3.5 h-3.5 text-slate-500 transition-transform ${sections.display ? '' : '-rotate-90'}`}
          />
        </div>
        <div className={`tp-section-body ${sections.display ? 'tp-section-open' : ''}`}>
          <div className="tp-section-content">
            {setMockupsPerRow && (
              <div className="tp-field-group">
                <div className="tp-field-label">
                  <Icon name="layout-grid" className="w-3 h-3" />
                  <span>Мокапов в строку</span>
                </div>
                <div className="tp-row-selector">
                  {[1, 2, 3].map((num) => (
                    <button
                      key={num}
                      onClick={() => setMockupsPerRow(num)}
                      className={`tp-row-btn ${mockupsPerRow === num ? 'active' : ''}`}
                    >
                      {num}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {setMaskColor && (
              <div className="tp-field-group">
                <div className="tp-field-label">
                  <Icon name="scan" className="w-3 h-3" />
                  <span>Граница маски</span>
                  {maskColor && (
                    <button
                      onClick={() => setMaskColor(null)}
                      className="tp-reset-link"
                      title="Сбросить"
                    >
                      <Icon name="x" className="w-3 h-3" />
                    </button>
                  )}
                </div>
                <div className="tp-color-picker">
                  {['#ffffff', '#ff0000', '#00ff00', '#0000ff', '#000000'].map((color) => (
                    <button
                      key={color}
                      onClick={() => setMaskColor(color === maskColor ? null : color)}
                      className={`tp-color-swatch ${maskColor === color ? 'active' : ''}`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Transform Section */}
      <div className="tp-section">
        <div onClick={() => toggleSection('transform')} className="tp-section-header" role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && toggleSection('transform')}>
          <div className="flex items-center gap-2">
            <Icon name="move" className="w-3.5 h-3.5 text-indigo-400" />
            <span>Трансформация</span>
          </div>
          <div className="flex items-center gap-2">
            {isActive && (
              <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                <button
                  onClick={copyTransform}
                  className={`tp-icon-btn-sm ${copyStatus === 'success' ? 'text-green-400' : ''}`}
                  title="Копировать"
                >
                  <Icon name={copyStatus === 'success' ? 'check' : 'copy'} className="w-3 h-3" />
                </button>
                <button
                  onClick={pasteTransform}
                  className={`tp-icon-btn-sm ${pasteStatus === 'success' ? 'text-green-400' : ''}`}
                  title="Вставить"
                >
                  <Icon
                    name={pasteStatus === 'success' ? 'check' : 'clipboard'}
                    className="w-3 h-3"
                  />
                </button>
              </div>
            )}
            <Icon
              name="chevron-down"
              className={`w-3.5 h-3.5 text-slate-500 transition-transform ${sections.transform ? '' : '-rotate-90'}`}
            />
          </div>
        </div>
        <div className={`tp-section-body ${sections.transform ? 'tp-section-open' : ''}`}>
          <div className="tp-section-content">
            {!isActive ? (
              <div className="tp-empty-state">
                <Icon name="mouse-pointer-click" className="w-7 h-7 mb-1.5" />
                <span>Выберите мокап</span>
              </div>
            ) : (
              <>
                <div className="tp-grid-2">
                  <div className="tp-numeric-field">
                    <span className="tp-numeric-label">X</span>
                    <div className="tp-stepper">
                      <button
                        onClick={() => onUpdateTransform({ ...t, x: t.x - 10 })}
                        className="tp-stepper-btn"
                      >
                        <Icon name="minus" className="w-3 h-3" />
                      </button>
                      <div className="tp-stepper-value">
                        <input
                          type="number"
                          value={Math.round(t.x)}
                          onChange={(e) =>
                            onUpdateTransform({ ...t, x: parseFloat(e.target.value) })
                          }
                          className="tp-stepper-input no-spinner w-10"
                        />
                        <span className="tp-stepper-unit">px</span>
                      </div>
                      <button
                        onClick={() => onUpdateTransform({ ...t, x: t.x + 10 })}
                        className="tp-stepper-btn"
                      >
                        <Icon name="plus" className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                  <div className="tp-numeric-field">
                    <span className="tp-numeric-label">Y</span>
                    <div className="tp-stepper">
                      <button
                        onClick={() => onUpdateTransform({ ...t, y: t.y - 10 })}
                        className="tp-stepper-btn"
                      >
                        <Icon name="minus" className="w-3 h-3" />
                      </button>
                      <div className="tp-stepper-value">
                        <input
                          type="number"
                          value={Math.round(t.y)}
                          onChange={(e) =>
                            onUpdateTransform({ ...t, y: parseFloat(e.target.value) })
                          }
                          className="tp-stepper-input no-spinner w-10"
                        />
                        <span className="tp-stepper-unit">px</span>
                      </div>
                      <button
                        onClick={() => onUpdateTransform({ ...t, y: t.y + 10 })}
                        className="tp-stepper-btn"
                      >
                        <Icon name="plus" className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
                <div className="tp-grid-2">
                  <div className="tp-numeric-field">
                    <span className="tp-numeric-label">Масштаб</span>
                    <div className="tp-stepper">
                      <button
                        onClick={() =>
                          onUpdateTransform({ ...t, scale: Math.max(0.05, t.scale - 0.01) })
                        }
                        className="tp-stepper-btn"
                      >
                        <Icon name="minus" className="w-3 h-3" />
                      </button>
                      <div className="tp-stepper-value">
                        <input
                          type="number"
                          value={Math.round(t.scale * 100)}
                          onChange={(e) =>
                            onUpdateTransform({ ...t, scale: parseFloat(e.target.value) / 100 })
                          }
                          className="tp-stepper-input no-spinner w-14"
                        />
                        <span className="tp-stepper-unit">%</span>
                      </div>
                      <button
                        onClick={() =>
                          onUpdateTransform({ ...t, scale: Math.min(10, t.scale + 0.01) })
                        }
                        className="tp-stepper-btn"
                      >
                        <Icon name="plus" className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                  {onDPIChange && (
                    <div className="tp-numeric-field">
                      <span className="tp-numeric-label">DPI</span>
                      <div className="tp-stepper">
                        <button
                          onClick={() => onDPIChange(Math.max(72, (dpi || 300) - 50))}
                          className="tp-stepper-btn"
                        >
                          <Icon name="minus" className="w-3 h-3" />
                        </button>
                        <div className="tp-stepper-value">
                          <input
                            type="number"
                            value={dpi || 300}
                            onChange={(e) => onDPIChange(parseInt(e.target.value))}
                            className="tp-stepper-input no-spinner w-14"
                          />
                        </div>
                        <button
                          onClick={() => onDPIChange(Math.min(600, (dpi || 300) + 50))}
                          className="tp-stepper-btn"
                        >
                          <Icon name="plus" className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                <input
                  type="range"
                  min="0.05"
                  max="10"
                  step="0.01"
                  value={t.scale}
                  onChange={(e) => onUpdateTransform({ ...t, scale: parseFloat(e.target.value) })}
                  className="tp-range-slider"
                />
                <div className="tp-numeric-field">
                  <span className="tp-numeric-label">Поворот</span>
                  <div className="tp-stepper">
                    <button onClick={() => rotateBy(-1)} className="tp-stepper-btn">
                      <Icon name="minus" className="w-3 h-3" />
                    </button>
                    <div className="tp-stepper-value">
                      <input
                        type="number"
                        value={Math.round(t.rotation)}
                        onChange={(e) =>
                          onUpdateTransform({ ...t, rotation: parseFloat(e.target.value) })
                        }
                        className="tp-stepper-input no-spinner w-10"
                      />
                      <span className="tp-stepper-unit">°</span>
                    </div>
                    <button onClick={() => rotateBy(1)} className="tp-stepper-btn">
                      <Icon name="plus" className="w-3 h-3" />
                    </button>
                  </div>
                </div>
                <div className="tp-quick-rotate">
                  {[
                    [-90, 'rotate-ccw'],
                    [-45, 'rotate-ccw'],
                    [0, 'refresh-ccw'],
                    [45, 'rotate-cw'],
                    [90, 'rotate-cw'],
                  ].map(([deg, icon]) => (
                    <button
                      key={deg as number}
                      onClick={() =>
                        deg === 0
                          ? onUpdateTransform({ ...t, rotation: 0 })
                          : rotateBy(deg as number)
                      }
                      className={`tp-rotate-btn ${deg === 0 ? 'tp-rotate-reset' : ''}`}
                      title={`${deg}°`}
                    >
                      <Icon name={icon as string} className="w-3.5 h-3.5" />
                      {deg !== 0 && (
                        <span>
                          {(deg as number) > 0 ? '+' : ''}
                          {deg}°
                        </span>
                      )}
                    </button>
                  ))}
                </div>
                <div className="tp-action-row">
                  <button
                    onClick={() => onUpdateTransform({ ...t, x: 0, y: 0 })}
                    className="tp-action-btn"
                    title="Центрировать"
                  >
                    <Icon name="crosshair" className="w-3.5 h-3.5" />
                    <span>Центр</span>
                  </button>
                  <button
                    onClick={() => onUpdateTransform({ x: 0, y: 0, scale: 1, rotation: 0 })}
                    className="tp-action-btn tp-action-danger"
                    title="Сбросить всё"
                  >
                    <Icon name="x-circle" className="w-3.5 h-3.5" />
                    <span>Сброс</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Presets Section */}
      {onSavePreset && (
        <div className="tp-section">
          <div onClick={() => toggleSection('presets')} className="tp-section-header" role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && toggleSection('presets')}>
            <div className="flex items-center gap-2">
              <Icon name="save" className="w-3.5 h-3.5 text-indigo-400" />
              <span>Конфиги</span>
            </div>
            <Icon
              name="chevron-down"
              className={`w-3.5 h-3.5 text-slate-500 transition-transform ${sections.presets ? '' : '-rotate-90'}`}
            />
          </div>
          <div className={`tp-section-body ${sections.presets ? 'tp-section-open' : ''}`}>
            <div className="tp-section-content">
              <div className="tp-preset-save">
                <input
                  type="text"
                  className="tp-preset-input"
                  placeholder="Имя конфига..."
                  value={presetName}
                  onChange={(e) => setPresetName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSaveClick('single')}
                />
                <div className="tp-preset-save-btns">
                  <button
                    onClick={() => handleSaveClick('single')}
                    className="tp-preset-save-btn tp-preset-single"
                    title="Для текущего мокапа"
                  >
                    <Icon name="bookmark" className="w-3 h-3" />
                    <span>Одиночный</span>
                  </button>
                  <button
                    onClick={() => handleSaveClick('all')}
                    className="tp-preset-save-btn tp-preset-all"
                    title="Для всех мокапов"
                  >
                    <Icon name="layers" className="w-3 h-3" />
                    <span>Все сразу</span>
                  </button>
                </div>
              </div>
              {presets && Object.keys(presets).length > 0 && (
                <div className="tp-preset-list">
                  <div className="tp-preset-list-header">
                    <span>Сохранённые</span>
                    <span className="tp-preset-count">{Object.keys(presets).length}</span>
                  </div>
                  <div className="tp-preset-scroll">
                    {Object.keys(presets).map((name) => {
                      const p = presets[name];
                      const isSingle =
                        p &&
                        typeof p === 'object' &&
                        ('x' in (p as Record<string, unknown>) ||
                          'scale' in (p as Record<string, unknown>));
                      return (
                        <div key={name} className="tp-preset-item group">
                          <div className="tp-preset-name" title={name}>
                            {name}
                          </div>
                          <div className="tp-preset-actions">
                            {isSingle ? (
                              <>
                                <button
                                  onClick={() => onApplyPreset?.(name, false)}
                                  className="tp-preset-apply"
                                  title="Применить к текущему"
                                >
                                  <Icon name="square" className="w-3 h-3" />
                                </button>
                                <button
                                  onClick={() => onApplyPreset?.(name, true)}
                                  className="tp-preset-apply-all"
                                  title="Применить ко всем"
                                >
                                  <Icon name="layers" className="w-3 h-3" />
                                </button>
                              </>
                            ) : (
                              <button
                                onClick={() => onApplyPreset?.(name)}
                                className="tp-preset-apply"
                                title="Применить набор"
                              >
                                <Icon name="layout-grid" className="w-3 h-3" />
                              </button>
                            )}
                            <button
                              onClick={() => onDeletePreset?.(name)}
                              className="tp-preset-delete"
                              title="Удалить"
                            >
                              <Icon name="x" className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
